import { cn, NoddiIcon } from 'noddi-ui-common';
import { Popover as PopoverPrimitive, Tooltip as TooltipPrimitive } from 'radix-ui';
import * as React from 'react';
import { useIsWithinTailWindBreakpoint } from '../../hooks/useIsWithinTailWindBreakpoint';

export function NoddiTooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot='tooltip-provider' delayDuration={delayDuration} {...props} />;
}

function NoddiTooltipTrigger({
  asChild = false,
  onFocusCapture,
  tabIndex,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      asChild={asChild}
      onFocusCapture={(e) => {
        e.stopPropagation();
        onFocusCapture?.(e);
      }}
      type={asChild ? undefined : 'button'}
      tabIndex={tabIndex ?? 0}
      data-slot='tooltip-trigger'
      {...props}
    />
  );
}

// Content never spills past the box or the screen: long words break, a row too wide to wrap is
// clipped, and content taller than the room the screen leaves scrolls inside the tooltip.
function NoddiTooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot='tooltip-content'
        sideOffset={sideOffset}
        collisionPadding={16}
        avoidCollisions
        className={cn(
          'z-overlay max-h-(--radix-tooltip-content-available-height) w-fit max-w-[250px] origin-(--radix-tooltip-content-transform-origin) animate-in overflow-x-hidden overflow-y-auto rounded-md border bg-white px-3 py-1.5 text-xs text-balance wrap-break-word drop-shadow-xl fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

// A bit hacky, but tooltip doesn't work for mobile, so styling popover to look like tooltip and adds hover so it behaves like a tooltip on desktop small screens
function PopoverContentForToolTip({
  className,
  align = 'center',
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot='popover-content'
        align={align}
        sideOffset={sideOffset}
        collisionPadding={16}
        avoidCollisions
        className={cn(
          'z-overlay max-h-(--radix-popover-content-available-height) w-fit max-w-[250px] origin-(--radix-popover-content-transform-origin) animate-in overflow-x-hidden overflow-y-auto rounded-md border bg-white px-3 py-1.5 text-xs text-balance wrap-break-word drop-shadow-xl fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className
        )}
        {...props}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

type NoddiTooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root> & {
  content: React.ReactNode;
  trigger?: React.ReactNode;
  triggerClassName?: string;
  tooltipContentWrapperClassName?: string;
  /**
   * When true (default), blocks pointer events on the trigger so tooltips
   * inside buttons/menus don't steal the click. Set false when the trigger
   * is itself a click target (e.g. a checkbox label that should still toggle).
   */
  stopEventPropagation?: boolean;
  label?: string;
};

export function NoddiTooltip({
  content,
  trigger,
  triggerClassName,
  tooltipContentWrapperClassName,
  stopEventPropagation = true,
  label = 'More information',
  ...props
}: NoddiTooltipProps) {
  const isSmallScreen = useIsWithinTailWindBreakpoint('md');
  const [popoverOpen, setPopoverOpen] = React.useState(false);

  const stopAncestorPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

  // React halts the whole dispatch when a capture handler stops propagation, so a capture-phase
  // shield also swallows the trigger's own bubble handlers. Desktop never noticed - hover opens the
  // tooltip - but the small-screen popover opens from the primitive's onClick, so a tap did nothing.
  // Bubble phase still shields ancestors: Slot runs our handler before the primitive's.
  const ancestorShieldProps = !stopEventPropagation
    ? {}
    : isSmallScreen
      ? { onPointerDown: stopAncestorPropagation, onClick: stopAncestorPropagation }
      : { onPointerDownCapture: stopAncestorPropagation, onClickCapture: stopAncestorPropagation };

  const triggerNode = (
    <span
      className={cn('inline-flex items-center', triggerClassName)}
      role='button'
      tabIndex={0}
      aria-label={label}
      // Capture phase: NoddiTooltipTrigger stops focus propagation in its own capture handler,
      // which also swallows same-element bubble handlers, so onFocus would never fire on desktop.
      onFocusCapture={() => setPopoverOpen(true)}
      onBlurCapture={() => setPopoverOpen(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setPopoverOpen(true);
        } else if (event.key === 'Escape') {
          setPopoverOpen(false);
        }
      }}
      {...ancestorShieldProps}
    >
      {trigger ?? <NoddiIcon name='Info' size='xSmall' />}
    </span>
  );

  const handleMouseEnter = () => {
    setPopoverOpen(true);
  };

  const handleMouseLeave = () => {
    setPopoverOpen(false);
  };

  if (isSmallScreen) {
    return (
      <PopoverPrimitive.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverPrimitive.Trigger
          asChild
          data-slot='popover-trigger'
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {triggerNode}
        </PopoverPrimitive.Trigger>
        <PopoverContentForToolTip
          className={tooltipContentWrapperClassName}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </PopoverContentForToolTip>
      </PopoverPrimitive.Root>
    );
  }

  return (
    <TooltipPrimitive.Provider delayDuration={0}>
      {/* Controlled so keyboard focus (tracked via popoverOpen) opens it too; hover still works
          because Radix routes its internal hover open/close through onOpenChange. */}
      <TooltipPrimitive.Root data-slot='tooltip' {...props} open={popoverOpen} onOpenChange={setPopoverOpen}>
        <NoddiTooltipTrigger asChild>{triggerNode}</NoddiTooltipTrigger>
        <NoddiTooltipContent className={tooltipContentWrapperClassName}>{content}</NoddiTooltipContent>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

type NoddiHoverTooltipProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
};

export function NoddiHoverTooltip({
  content,
  children,
  className,
  contentClassName = 'max-w-sm'
}: NoddiHoverTooltipProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger asChild>
          <div className={cn('inline-flex max-w-full', className)} onPointerDown={() => setOpen(false)}>
            {children}
          </div>
        </TooltipPrimitive.Trigger>
        <NoddiTooltipContent className={contentClassName}>{content}</NoddiTooltipContent>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
